import { createContext, useContext } from 'react'
import { createPortal } from 'react-dom'

import { IComponentsHeader } from './types'
import { useWindowSize } from '@hooks/useWindowSize'

import { Flex, Input, Button, Divider, ToolTip, Badge, Container } from '@ui/index'
import {
    IconAdd, IconClearClose, IconFilter, IconJobs, IconPromo,
    IconQuestion, IconReports, IconSearch, IconUpdate
} from '@img/icons'

import css from './index.module.scss'
import { useDeviceType } from '@hooks/useDeviceType'


export const PageHeaderPortalContext = createContext<HTMLElement | null | undefined>(undefined)


/** Заголовки в компонентах */
export const ComponentsHeader = ({ 
    inline = false, headerIcon, headerTitle, actions, txtSearch, txtSearchTooltip, filterIsActivate, isAccessDenied,

    txtSearchOnChange, txtSearchClearOnClick,
    btnUpdateOnClick, btnReportsOnClick, btnJobsOnClick, btnJobsCount,
    btnFilterOnClick, btnFilterClearOnClick,
    btnCreateOnClick, btnPromoOnClick
}: IComponentsHeader) => {
    const deviceType = useDeviceType()
    const { width } = useWindowSize()
    const pageHeaderPortalTarget = useContext(PageHeaderPortalContext)

    const headerContent = <>
            <Flex gap="5px" flexDirection="row" alignItems="center">
                {headerIcon}
                <h4>{headerTitle}</h4>
            </Flex>

            <Flex gap="8px" flexDirection="row" alignItems="center">
                {actions}

                {txtSearchOnChange &&
                    <Input isRendered={width <= 650 ? false : true} isDisabled={isAccessDenied}
                        size={deviceType === 'mobile' ? 'sm' : 'md'} paddingLeft='4px' paddingRight="5px" placeholder="Поиск..."
                        prefix={<IconSearch width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} color="var(--foreground_accent_default)" />}
                        value={txtSearch} onChange={txtSearchOnChange}
                        suffix={<>
                            <ToolTip title={txtSearchTooltip} placement="bottomRight">
                                <IconQuestion width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} color="var(--foreground_accent_default)" />
                            </ToolTip>

                            {txtSearch &&
                                <ToolTip title='Очистить' placement="bottomRight">
                                    <Button size={deviceType === 'mobile' ? 'xs' : 'sm'} variant="iconInput" onClick={txtSearchClearOnClick}>
                                        <IconClearClose width={deviceType === 'mobile' ? '16px' : '18px'} height={deviceType === 'mobile' ? '16px' : '18px'} />
                                    </Button>
                                </ToolTip>
                            }
                        </>}
                    />
                }

                {btnReportsOnClick &&
                    <ToolTip title='Отчёты' placement="bottomRight">
                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnReportsOnClick} isDisabled={isAccessDenied}>
                            <IconReports width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                        </Button>
                    </ToolTip>
                }

                {btnUpdateOnClick && 
                    <ToolTip title='Обновить' placement="bottomRight">
                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnUpdateOnClick} isDisabled={isAccessDenied}>
                            <IconUpdate width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                        </Button>
                    </ToolTip>
                }

                {btnJobsOnClick &&
                    <ToolTip title='Работы для проверки' placement="bottomRight">
                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnJobsOnClick} isDisabled={isAccessDenied}>
                            <IconJobs width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                            <Badge isVisible={Boolean((btnJobsCount || 0) > 0)} top="-4px" right="-4px" background="var(--foreground_accent_default)">{btnJobsCount}</Badge>
                        </Button>
                    </ToolTip>
                }
                
                {btnFilterOnClick &&
                    <div style={{ position: 'relative' }}>
                        <ToolTip title='Фильтр' placement="bottomRight">
                            <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnFilterOnClick} isDisabled={isAccessDenied}>
                                <IconFilter width={deviceType === 'mobile' ? '16px' : '22px'} height={deviceType === 'mobile' ? '16px' : '22px'} />
                            </Button>
                        </ToolTip>

                        {filterIsActivate === true &&
                            <ToolTip title='Очистить фильтр' placement="bottomRight">
                                <Button variant="primary" size="clear" maxWidth="10px" position="absolute" right="-1px" bottom="-1px" onClick={btnFilterClearOnClick} isDisabled={isAccessDenied}>
                                    <IconClearClose width="15px" height="15px" color='var(--color_txt_default)' />
                                </Button>
                            </ToolTip>
                        }
                    </div>
                }

                {btnCreateOnClick && 
                    <ToolTip title='Создать' placement="bottomRight">
                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnCreateOnClick} isDisabled={isAccessDenied}>
                            <IconAdd width={deviceType === 'mobile' ? '20px' : '24px'} height={deviceType === 'mobile' ? '20px' : '24px'} />
                        </Button>
                    </ToolTip>
                }

                {(btnPromoOnClick) &&
                    <Divider type="vertical" size={deviceType === 'mobile' ? '16px' : '20px'} variant={localStorage.getItem("theme") === 'light' ? 'dark' : 'light'} />
                }

                {btnPromoOnClick &&
                    <ToolTip title='Промо лист' placement="bottomRight">
                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'md'} onClick={btnPromoOnClick} isDisabled={isAccessDenied}>
                            <IconPromo width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                        </Button>
                    </ToolTip>
                }
            </Flex>

            {txtSearchOnChange && 
                <Input isRendered={width <= 650 ? true : false} width="100%" isDisabled={isAccessDenied}
                    size={deviceType === 'mobile' ? 'sm' : 'md'} paddingLeft='4px' paddingRight="5px" placeholder="Поиск..."
                    prefix={<IconSearch width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} color="var(--foreground_accent_default)" />}
                    value={txtSearch} onChange={txtSearchOnChange}
                    suffix={<>
                        <ToolTip title={txtSearchTooltip} placement="bottomRight">
                            <IconQuestion width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} color="var(--foreground_accent_default)" />
                        </ToolTip>

                        {txtSearch &&
                            <ToolTip title='Очистить' placement="bottomRight">
                                <Button size={deviceType === 'mobile' ? 'xs' : 'sm'} variant="iconInput" onClick={txtSearchClearOnClick}>
                                    <IconClearClose width={deviceType === 'mobile' ? '16px' : '18px'} height={deviceType === 'mobile' ? '16px' : '18px'} />
                                </Button>
                            </ToolTip>
                        }
                    </>}
                />
            }
    </>

    const header = inline
        ? <div className={css.header}>{headerContent}</div>
        : <Container className={css.header}>{headerContent}</Container>

    if (!inline && width > 520 && pageHeaderPortalTarget !== undefined) {
        return pageHeaderPortalTarget ? createPortal(header, pageHeaderPortalTarget) : null
    }

    return header
}